"use client"

import { Button } from "@/components/ui/button"
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Archive, ArrowLeft, Battery, Folder, Search, Signal, SquarePen, Wifi } from "lucide-react"
import Image from "next/image"
import { useSearchParams } from 'next/navigation'
import { Suspense, useEffect, useState } from "react"
import "./styles.css"

interface Product {
  id: string
  name: string
  description: string
  originalPrice: number
  salePrice: number
  image: string
  images?: string[]
  variants?:string[]
  sizes?: string[]
}

interface SelectedProduct extends Product {
  selectedVariant: string
  selectedSize: string
  quantity: number
}

// Add Safari detection function
function isSafari() {
  if (typeof window === 'undefined') return false;
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

function IPhoneEmailPurchaseContent() {
  const searchParams = useSearchParams()
  const isAutoMode = searchParams.get('auto') === '1'
  const [selectedVariant, setselectedVariant] = useState("Yellow")
  const [selectedSize, setSelectedSize] = useState("Medium")
  const [discountCode, setDiscountCode] = useState("code10")
  const [isDiscountApplied, setIsDiscountApplied] = useState(false)
  const [isPromoClicked, setIsPromoClicked] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [isConfiguring, setIsConfiguring] = useState(false)
  const [configuringProduct, setConfiguringProduct] = useState<Product | null>(null)
  const [tempColor, setTempColor] = useState("")
  const [tempSize, setTempSize] = useState("")
  const [isAddingToCart, setIsAddingToCart] = useState<string | null>(null)
  const [isRemovingFromCart, setIsRemovingFromCart] = useState<string | null>(null)
  const [isEmailClicked, setIsEmailClicked] = useState(false)
  const [showCursor, setShowCursor] = useState(false)
  const [isSafariBrowser, setIsSafariBrowser] = useState(false);

  useEffect(() => {
    // Only start the cursor animation sequence in auto mode
    if (isAutoMode) {
      // Show cursor and start its animation
      setShowCursor(true);
      
      // Trigger the slide right after cursor animation completes
      const timer = setTimeout(() => {
        setIsEmailClicked(true);
      }, 4500); // Match the new cursor animation duration
      
      return () => clearTimeout(timer);
    }
  }, [isAutoMode]);

  useEffect(() => {
    setIsSafariBrowser(isSafari());
  }, []);

  const products: Product[] = [
    {
      id: "antioxidant-serum",
      name: "Vitamin C Brightening Serum",
      originalPrice: 49.99,
      salePrice: 44.99,
      image: "/iframe/rec1.png", 
      description: "Powerful antioxidant serum with 20% Vitamin C, Vitamin E, and Hyaluronic Acid for radiant, youthful skin",
      variants: ["Regular Strength (10% Vitamin C)", "Extra Strength (20% Vitamin C)", "Sensitive Skin Formula (5% Vitamin C)"],
      sizes: ["0.5 fl oz", "1 fl oz", "2 fl oz"],
    },
    {
      id: "hydra-cleanse",
      name: "Hydrating Foam Cleanser",
      originalPrice: 29.99,
      salePrice: 24.99,
      image: "/iframe/rec2.png",
      description: "Gentle, sulfate-free foaming cleanser with ceramides and niacinamide that removes makeup while maintaining skin barrier",
      sizes: ["4 fl oz", "8 fl oz", "16 fl oz"],
    },
    {
      id: "night-renew-cream",
      name: "Retinol Night Renewal Cream",
      originalPrice: 39.99,
      salePrice: 34.99,
      image: "/iframe/rec3.png",
      description: "Advanced anti-aging night cream with encapsulated retinol, peptides, and bakuchiol for smoother, firmer skin",
      variants: ["Beginner Formula (0.25% Retinol)", "Advanced Formula (1% Retinol)"],
    },
    {
      id: "soothing-toner",
      name: "Calming Rose Water Toner",
      originalPrice: 24.99,
      salePrice: 19.99,
      image: "/iframe/rec4.png",
      description: "Alcohol-free hydrating toner with rose water, aloe vera, and chamomile extract to balance and soothe skin",
    }
  ]
  const mainProduct = {
    id: "gentle-face-cleanser",
    name: "Gentle Face Cleanser",
    description: "Hydrating Daily Facial Cleanser for All Skin Types",
    originalPrice: 34.99,
    salePrice: 29.99,
    image: "/iframe/main1.png",
    images: ["/iframe/main1.png", "/iframe/main2.png", "/iframe/main3.png", "/iframe/main4.png"],
    variants: ["Fragrance Free", "Rose", "Lavender", "Citrus"], 
    sizes: ["2 fl oz", "4 fl oz", "8 fl oz", "16 fl oz"]
  }


  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([{
    ...mainProduct,
    selectedVariant: mainProduct.variants?.[0] || '',
    selectedSize: mainProduct.sizes?.[0] || '',
    quantity: 2
  }])
  const [recommendedProducts, setRecommendedProducts] = useState<SelectedProduct[]>(
    products.filter((product) => product.id !== mainProduct.id).map(product => ({
      ...product,
      selectedVariant: product.variants?.[0] || '',
      selectedSize: product.sizes?.[0] || '',
      quantity: 1
    }))
  )

  const subtotal =
    selectedProducts.reduce((sum, product) => sum + product.salePrice * product.quantity, 0)

  const shipping = subtotal === 0 ? 0 : 5.0
  const taxRate = 0.21
  const taxes = subtotal === 0 ? 0 : (subtotal + shipping) * taxRate
  const discountAmount = subtotal === 0 ? 0 : (isDiscountApplied ? subtotal * 0.15 : 0)
  const total = subtotal + shipping + taxes - discountAmount

  const addToCart = (productId: string) => {
    const productToAdd = recommendedProducts.find(p => p.id === productId)
    if (!productToAdd) return

    setSelectedProducts(prev => [...prev, productToAdd])
    setRecommendedProducts(prev => prev.filter(p => p.id !== productId))
  }

  const updateSelectedProduct = (productId: string, field: 'selectedVariant' | 'selectedSize' | 'quantity', value: string | number) => {
    setSelectedProducts(prev => prev.map(product => 
      product.id === productId 
        ? { ...product, [field]: value }
        : product
    ))
  }

  const applyDiscount = () => {
    if (discountCode.toLowerCase() === "code10") {
      setIsDiscountApplied(true)
    }
  }

  const handleRemoveProduct = (product: SelectedProduct) => {
    setIsRemovingFromCart(product.id);
    setTimeout(() => {
      setSelectedProducts(prev => prev.filter(p => p.id !== product.id));
      // Only add to recommendations if it's not the main product
      if (product.id !== mainProduct.id) {
        setRecommendedProducts(prev => [...prev, {
          ...product,
          quantity: 1 // Reset quantity to 1 when adding back to recommendations
        }]);
      }
      setIsRemovingFromCart(null);
    }, 600);
  }

  return (
    <div>
      {isAutoMode && showCursor && (
        <div className={`floating-cursor animate`} />
      )}
      <div id="wrapper" className="max-w-sm relative mx-auto bg-black rounded-[3rem] p-2 h-[800px]"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div className="bg-white rounded-[2.5rem] overflow-hidden h-full relative flex flex-col">
          {/* Status Bar - Shared */}
          <div className="flex justify-between items-center px-6 py-2 text-black text-sm font-medium">
            <span>9:41</span>
            <div className="w-20 h-6 bg-black rounded-full"></div>
            <div className="flex items-center gap-1">
              <Signal className="w-4 h-4" />
              <Wifi className="w-4 h-4" />
              <Battery className="w-6 h-3" />
            </div>
          </div>

          {/* Email Header - Shared */}
          <div className="flex items-center justify-between px-4 py-2 border-b">
            {!isEmailClicked ? (
              // Initial view with Inboxes, Select, and dots
              <>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" className="text-blue-500 px-2 h-8">
                    Inboxes
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" className="text-blue-500 px-2 h-8">
                    Select
                  </Button>
                  <Button variant="ghost" className="text-blue-500 px-2 h-8">
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                      <circle cx="5" cy="12" r="2" />
                      <circle cx="12" cy="12" r="2" />
                      <circle cx="19" cy="12" r="2" />
                    </svg>
                  </Button>
                </div>
              </>
            ) : (
              // Email detail view with back button and action buttons
              <>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    className="text-blue-500 px-2 h-8 flex items-center gap-1"
                    // onClick={() => setIsEmailClicked(false)}
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" className="text-blue-500 px-2 h-8">
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </Button>
                  <Button variant="ghost" className="text-blue-500 px-2 h-8 rotate-180">
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Main Content Area with Sliding Animation */}
          <div className="main-content-wrapper">
            {/* Sliding Email List */}
            <div className={`sliding-content ${isEmailClicked ? 'animate' : ''}`}>
              {/* Title */}
              <h1 className="text-4xl font-bold px-4 pt-2 pb-0">All Mail</h1>

              {/* Search Bar */}
              <div className="px-4 py-2 border-b">
                <div className="bg-gray-100 rounded-lg px-3 py-2 flex items-center gap-2">
                  <Search className="w-4 h-4 text-gray-400" />
                  <input 
                    type="text" 
                    placeholder="Search" 
                    className="bg-transparent border-none outline-none text-sm w-full placeholder:text-gray-400"
                    readOnly
                  />
                </div>
              </div>
              
              <img src="/iframe/button-part.jpeg" alt="email-preview" className="w-full p-2" />

              {/* Cart Recovery Email */}
              <div className="relative">
                <div 
                  className="email-preview cursor-pointer hover:bg-gray-50 relative transition-colors pulse-highlight" 
                  style={{
                    paddingLeft: "25px"
                  }}
                  onClick={() => setIsEmailClicked(true)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="absolute left-[5px] top-[35px] transform translate-y-[-50%]">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  </div>
                  <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                    <Image src="/iframe/logo.png" alt="kinetic-logo" width={100} height={100} className="w-full h-full rounded-full" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <h3 className="font-semibold text-sm truncate">Kinetic Store</h3>
                      <span className="text-xs text-gray-500 flex-shrink-0">8:43 AM</span>
                    </div>
                    <h4 className="font-medium text-sm text-gray-900">Complete your purchase</h4>
                    <p className="text-sm text-gray-500 truncate">You left some amazing products in your cart. Finish your order now before they are gone.</p>
                  </div>
                </div>
                
                {!isAutoMode && (
                  <div className="click-hint-container">
                    <div className="click-indicator" />
                    <span className="click-hint-text" style={{
                      marginBottom: "-45px",
                      padding: "14px 20px",
                      marginLeft: "16px",
                      fontSize: "18px",
                      textAlign: "center",
                      display: "inline-block",
                      lineHeight: "1.4",
                      fontWeight: "700"
                    }}>Tap & try out <br />an interactive email</span>
                  </div>
                )}
              </div>

              {/* Other Emails */}
              <div className="email-preview grayed cursor-pointer hover:bg-gray-50 transition-colors" style={{paddingLeft: "25px"}}>
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-white font-medium">N</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-sm truncate">Newsletter #1</h3>
                    <span className="text-xs text-gray-500 flex-shrink-0">8:15 AM</span>
                  </div>
                  <h4 className="font-medium text-sm text-gray-900">Your Weekly Beauty Tips</h4>
                  <p className="text-sm text-gray-500 truncate">Discover the latest trends in skincare and beauty. This week: The power of Vitamin C...</p>
                </div>
              </div>

              <div className="email-preview grayed cursor-pointer hover:bg-gray-50 transition-colors" style={{paddingLeft: "25px"}}>
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-white font-medium">N</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-sm truncate">Newsletter #2</h3>
                    <span className="text-xs text-gray-500 flex-shrink-0">7:30 AM</span>
                  </div>
                  <h4 className="font-medium text-sm text-gray-900">Your Evening Routine Guide</h4>
                  <p className="text-sm text-gray-500 truncate">Learn how to maximize the benefits of your skincare products with this step-by-step guide...</p>
                </div>
              </div>

              <div className="email-preview grayed cursor-pointer hover:bg-gray-50 transition-colors" style={{paddingLeft: "25px"}}>
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-white font-medium">N</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-sm truncate">Newsletter #3</h3>
                    <span className="text-xs text-gray-500 flex-shrink-0">Yesterday</span>
                  </div>
                  <h4 className="font-medium text-sm text-gray-900">New Products Alert!</h4>
                  <p className="text-sm text-gray-500 truncate">Check out our latest arrivals: Revolutionary anti-aging serum and hydrating face masks...</p>
                </div>
              </div>

              <div className="email-preview grayed cursor-pointer hover:bg-gray-50 transition-colors" style={{paddingLeft: "25px"}}>
                <div className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-white font-medium">N</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-sm truncate">Newsletter #4</h3>
                    <span className="text-xs text-gray-500 flex-shrink-0">Yesterday</span>
                  </div>
                  <h4 className="font-medium text-sm text-gray-900">Summer Skincare Special</h4>
                  <p className="text-sm text-gray-500 truncate">Get ready for summer with our expert tips on sun protection and hydration...</p>
                </div>
              </div>

              <div className="email-preview grayed cursor-pointer hover:bg-gray-50 transition-colors" style={{paddingLeft: "25px"}}>
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-white font-medium">N</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-sm truncate">Newsletter #5</h3>
                    <span className="text-xs text-gray-500 flex-shrink-0">2 days ago</span>
                  </div>
                  <h4 className="font-medium text-sm text-gray-900">Customer Success Stories</h4>
                  <p className="text-sm text-gray-500 truncate">See the amazing transformations our customers have achieved with our products...</p>
                </div>
              </div>

              <div className="email-preview grayed cursor-pointer hover:bg-gray-50 transition-colors" style={{paddingLeft: "25px"}}>
                <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-white font-medium">N</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-sm truncate">Newsletter #6</h3>
                    <span className="text-xs text-gray-500 flex-shrink-0">3 days ago</span>
                  </div>
                  <h4 className="font-medium text-sm text-gray-900">Ingredient Spotlight</h4>
                  <p className="text-sm text-gray-500 truncate">Deep dive into the science behind retinol and why it's a skincare essential...</p>
                </div>
              </div>
            </div>

            {/* Cart Recovery Content */}
            <div className="h-full overflow-y-auto">
              {/* Configuration Modal */}
              {isConfiguring && configuringProduct && (
                <div className="absolute inset-0 bg-black/50 z-50 rounded-[2.5rem] flex items-center justify-center">
                  <div className="bg-white p-6 rounded-xl max-w-[80%] w-full">
                    <h3 className="font-medium text-lg mb-4">Configure {configuringProduct.name}</h3>
                    
                    {/* Color Selection */}
                    {configuringProduct.variants && configuringProduct.variants.length > 0 && (
                      <div className="mb-4">
                        <label className="text-sm text-gray-600 mb-2 block">Select Color</label>
                        {isSafariBrowser ? (
                          <select
                            value={tempColor}
                            onChange={(e) => setTempColor(e.target.value)}
                            className="w-full h-9 text-sm bg-white border rounded-md px-3"
                          >
                            {configuringProduct.variants.map((variant) => (
                              <option key={variant} value={variant}>
                                {variant}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Select value={tempColor} onValueChange={setTempColor}>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {configuringProduct.variants.map((variant) => (
                                <SelectItem key={variant} value={variant}>
                                  {variant}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}

                    {/* Size Selection */}
                    {configuringProduct.sizes && configuringProduct.sizes.length > 0 && (
                      <div className="mb-6">
                        <label className="text-sm text-gray-600 mb-2 block">Select Size</label>
                        {isSafariBrowser ? (
                          <select
                            value={tempSize}
                            onChange={(e) => setTempSize(e.target.value)}
                            className="w-full h-9 text-sm bg-white border rounded-md px-3"
                          >
                            {configuringProduct.sizes.map((size) => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Select value={tempSize} onValueChange={setTempSize}>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {configuringProduct.sizes.map((size) => (
                                <SelectItem key={size} value={size}>
                                  {size}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}

                    <div className="flex gap-3">
                      <Button 
                        className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800"
                        onClick={() => {
                          setIsConfiguring(false)
                          setConfiguringProduct(null)
                        }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        className="flex-1 "
                        onClick={() => {
                          setSelectedProducts(prev => [...prev, {
                            ...configuringProduct,
                            selectedVariant: tempColor,
                            selectedSize: tempSize,
                            quantity: 1
                          }])
                          setRecommendedProducts(prev => prev.filter(p => p.id !== configuringProduct.id))
                          setIsConfiguring(false)
                          setConfiguringProduct(null)
                        }}
                      >
                        Add to Cart
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Main Content */}
              <div className="space-y-6 p-4">
                {/* Sender Info */}
                <div className="sticky top-0 z-10 -mt-4 -mx-4 px-4 pt-4 pb-3 bg-white border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center shadow-sm">
                        <Image src="/iframe/logo.png" alt="kinetic-logo" width={100} height={100} className="w-full h-full rounded-full" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm">Kinetic Store</div>
                        <div className="text-xs text-gray-500">To: info@kinetic.com</div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">8:43 AM</span>
                  </div>
                </div>

                {/* Main Message */}
                <div className="space-y-2">
                  {selectedProducts.length > 0 ? (
                    <>
                      <h2 className="text-xl font-bold text-gray-900">Finish your order now.</h2>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        You left some amazing products in your cart. Finish your order now before they are gone.
                      </p>
                    </>
                  ) : (
                    <div className="text-center py-8 space-y-3">
                      <div className="text-4xl mb-4">🛒</div>
                      <h2 className="text-xl font-bold text-gray-900">Your cart is empty</h2>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        But don't worry, we have some recommendations for you below.
                      </p>
                    </div>
                  )}
                </div>

                {/* Selected Products */}
                {selectedProducts.length > 0 && (
                  <div className="space-y-6">
                    {selectedProducts.map((product, index) => (
                      <div key={product.id} className="bg-gray-50 rounded-xl p-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4 items-start">
                          <div className="relative aspect-square overflow-hidden rounded-xl shadow-sm">
                            {product.images && product.images.length > 1 ? (
                              <Carousel className="w-full h-full z-0">
                                <CarouselContent>
                                  {product.images.map((image, imageIndex) => (
                                    <CarouselItem key={imageIndex}>
                                      <div className="relative aspect-square overflow-hidden rounded-xl">
                                        <Image
                                          src={image || "/placeholder.svg"}
                                          alt={`${product.name} - Image ${imageIndex + 1}`}
                                          width={200}
                                          height={200}
                                          className="object-cover w-full h-full"
                                        />
                                      </div>
                                    </CarouselItem>
                                  ))}
                                </CarouselContent>
                                <CarouselPrevious className="carousel-arrow absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/60 hover:bg-white/80 border border-gray-300/50 shadow-md" />
                                <CarouselNext className="carousel-arrow absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/60 hover:bg-white/80 border border-gray-300/50 shadow-md" />
                              </Carousel>
                            ) : (
                              <Image
                                src={product.image || "/placeholder.svg"}
                                alt={product.name}
                                width={200}
                                height={200}
                                className="object-cover w-full h-full"
                              />
                            )}
                          </div>
                          <div className="space-y-3">
                            <div>
                              <h3 className="font-semibold text-gray-900">{product.name}</h3>
                              <p className="text-xs text-gray-500 mt-1">{product.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-400 line-through">{product.originalPrice.toFixed(2)}€</span>
                              <span className="text-sm font-bold text-gray-900">{product.salePrice.toFixed(2)}€</span>
                            </div>
                          </div>
                        </div>

                        <div className={`grid gap-3 ${
                          // Calculate grid columns based on available options
                          (product.variants && product.variants.length > 0) && (product.sizes && product.sizes.length > 0) 
                            ? 'grid-cols-2' // Both variants and sizes available
                            : (product.variants && product.variants.length > 0) || (product.sizes && product.sizes.length > 0)
                              ? 'grid-cols-2' // Only one of variants or sizes available
                              : 'grid-cols-1' // Only quantity available
                        }`}>
                          {/* Quantity and Remove */}
                          <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700">Quantity</label>
                            <div className="flex items-center gap-2">
                              {isSafariBrowser ? (
                                <select 
                                  value={product.quantity.toString()}
                                  onChange={(e) => {
                                    const quantity = Number(e.target.value);
                                    if (quantity === 0) {
                                      handleRemoveProduct(product);
                                    } else {
                                      updateSelectedProduct(product.id, 'quantity', quantity);
                                    }
                                  }}
                                  disabled={isRemovingFromCart === product.id}
                                  className="w-full h-9 text-sm bg-white border rounded-md px-3"
                                >
                                  <option value="0" className="text-red-600">Remove</option>
                                  {[1, 2, 3, 4, 5].map((num) => (
                                    <option key={num} value={num.toString()}>{num}</option>
                                  ))}
                                </select>
                              ) : (
                                <Select 
                                  value={isRemovingFromCart === product.id ? "0" : product.quantity.toString()}
                                  onValueChange={(value) => {
                                    const quantity = Number(value);
                                    if (quantity === 0) {
                                      handleRemoveProduct(product);
                                    } else {
                                      updateSelectedProduct(product.id, 'quantity', quantity);
                                    }
                                  }}
                                  disabled={isRemovingFromCart === product.id}
                                >
                                  <SelectTrigger className="w-full h-9 text-sm bg-white">
                                    {isRemovingFromCart === product.id ? (
                                      <div className="flex items-center justify-center w-full py-1">
                                        <div className="loading-spinner primary small" />
                                      </div>
                                    ) : (
                                      <SelectValue />
                                    )}
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="0" className="text-red-600 hover:bg-red-50">
                                      Remove
                                    </SelectItem>
                                    {[1, 2, 3, 4, 5].map((num) => (
                                      <SelectItem key={num} value={num.toString()}>
                                        {num}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          </div>

                          {/* Size Selection */}
                          {product.sizes && product.sizes.length > 0 && (
                            <div className={`space-y-2 ${
                              !(product.variants && product.variants.length > 0) 
                                ? 'col-span-2' // Full width when only sizes exist
                                : '' // Normal width when both exist
                            }`}>
                              <label className="text-xs font-medium text-gray-700">Size</label>
                              {isSafariBrowser ? (
                                <select
                                  value={product.selectedSize}
                                  onChange={(e) => updateSelectedProduct(product.id, 'selectedSize', e.target.value)}
                                  className="w-full h-9 text-sm bg-white border rounded-md px-3"
                                >
                                  {product.sizes?.map((size) => (
                                    <option key={size} value={size}>
                                      {size}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <Select 
                                  value={product.selectedSize} 
                                  onValueChange={(value) => updateSelectedProduct(product.id, 'selectedSize', value)}
                                >
                                  <SelectTrigger className="w-full h-9 text-sm bg-white">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {product.sizes?.map((size) => (
                                      <SelectItem key={size} value={size}>
                                        {size}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          )}

                          {/* Color Selection */}
                          {product.variants && product.variants.length > 0 && (
                            <div className={`space-y-2 col-span-2`}>
                              <label className="text-xs font-medium text-gray-700">Variant</label>
                              {isSafariBrowser ? (
                                <select
                                  value={product.selectedVariant}
                                  onChange={(e) => updateSelectedProduct(product.id, 'selectedVariant', e.target.value)}
                                  className="w-full h-9 text-sm bg-white border rounded-md px-3"
                                >
                                  {product.variants?.map((variant) => (
                                    <option key={variant} value={variant}>
                                      {variant}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <Select 
                                  value={product.selectedVariant} 
                                  onValueChange={(value) => updateSelectedProduct(product.id, 'selectedVariant', value)}
                                >
                                  <SelectTrigger className="w-full h-9 text-sm bg-white">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {product.variants?.map((variant) => (
                                      <SelectItem key={variant} value={variant}>
                                        {variant}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Product Recommendations */}
                {recommendedProducts.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900">
                      {selectedProducts.length === 0 ? 'Recommended Products' : 'Recommended for You'}
                    </h3>
                    {recommendedProducts.length <= 2 ? (
                      <div className="grid grid-cols-2 gap-3">
                        {recommendedProducts.map((product) => (
                          <div key={product.id} className="bg-white border rounded-xl  space-y-3">
                            <div className="relative aspect-square overflow-hidden rounded-t-xl">
                              <Image
                                src={product.image || "/placeholder.svg"}
                                alt={product.name}
                                width={200}
                                height={200}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="space-y-2 p-3">
                              <h4 className="font-medium text-sm text-gray-900">{product.name}</h4>
                              <div className="flex items-center gap-2">
                                {product.originalPrice !== product.salePrice && (
                                  <span className="text-xs text-gray-400 line-through">{product.originalPrice.toFixed(2)}€</span>
                                )}
                                <span className="text-sm font-bold text-gray-900">{product.salePrice.toFixed(2)}€</span>
                              </div>
                              <Button
                                className="w-full h-9 text-sm bg-gray-900 hover:bg-gray-800 text-white"
                                onClick={() => {
                                  setIsAddingToCart(product.id);
                                  setTimeout(() => {
                                    setSelectedProducts(prev => [...prev, product]);
                                    setRecommendedProducts(prev => prev.filter(p => p.id !== product.id));
                                    setIsAddingToCart(null);
                                  }, 600);
                                }}
                                disabled={isAddingToCart === product.id}
                              >
                                {isAddingToCart === product.id ? (
                                  <div className="loading-spinner white small" />
                                ) : (
                                  'Add to cart'
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="relative">
                        <Carousel
                          opts={{
                            align: "start",
                            loop: true,
                          }}
                          className="w-full z-0"
                        >
                          <CarouselContent className="-ml-2">
                            {recommendedProducts.map((product) => (
                              <CarouselItem key={product.id} className="pl-2 basis-[50%] lg:basis-[40%]">
                                <div className="bg-white border rounded-xl  space-y-3">
                                  <div className="relative aspect-square overflow-hidden  rounded-t-xl">
                                    <Image
                                      src={product.image || "/placeholder.svg"}
                                      alt={product.name}
                                      width={200}
                                      height={200}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <div className="space-y-2 p-3">
                                    <div className="h-12 flex items-start tooltip-container">
                                      <h4 className="font-medium text-sm text-gray-900 leading-tight">{product.name}</h4>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {product.originalPrice !== product.salePrice && (
                                        <span className="text-xs text-gray-400 line-through">{product.originalPrice.toFixed(2)}€</span>
                                      )}
                                      <span className="text-sm font-bold text-gray-900">{product.salePrice.toFixed(2)}€</span>
                                    </div>
                                    <Button
                                      className="w-full h-9 text-sm bg-gray-900 hover:bg-gray-800 text-white"
                                      onClick={() => {
                                        setIsAddingToCart(product.id);
                                        setTimeout(() => {
                                          setSelectedProducts(prev => [...prev, product]);
                                          setRecommendedProducts(prev => prev.filter(p => p.id !== product.id));
                                          setIsAddingToCart(null);
                                        }, 600);
                                      }}
                                      disabled={isAddingToCart === product.id}
                                    >
                                      {isAddingToCart === product.id ? (
                                        <div className="loading-spinner white small" />
                                      ) : (
                                        'Add to cart'
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              </CarouselItem>
                            ))}
                          </CarouselContent>
                          <CarouselPrevious className="carousel-arrow absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/60 hover:bg-white/80 border border-gray-300/50 shadow-md" />
                          <CarouselNext className="carousel-arrow absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/60 hover:bg-white/80 border border-gray-300/50 shadow-md" />
                        </Carousel>
                      </div>
                    )}
                  </div>
                )}

                {/* Promo Code Section */}
                {selectedProducts.length > 0 && (
                  <div className="bg-kinetic-pink/10 rounded-xl p-4 space-y-3">
                    <h3 className="font-semibold text-kinetic-magenta">Special Offer</h3>
                    <Button 
                      onClick={() => {
                        setDiscountCode("PROMO15");
                        setIsDiscountApplied(true);
                        setIsPromoClicked(true);
                        setTimeout(() => setIsPromoClicked(false), 2000);
                      }} 
                      disabled={isDiscountApplied}
                      className={`w-full h-12 black-cursor font-medium flex items-center justify-center gap-2 transition-all duration-300 rounded-xl
                        ${isPromoClicked 
                          ? 'bg-green-500 hover:bg-green-600 text-white' 
                          : isDiscountApplied 
                            ? 'bg-gray-200 text-gray-500' 
                            : 'bg-kinetic-pink text-kinetic-magenta hover:bg-kinetic-pink/90'}`}
                    >
                      {isPromoClicked ? (
                        <>
                          <svg
                            className="w-6 h-6 checkmark"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          Promo Applied!
                        </>
                      ) : isDiscountApplied ? (
                        <>
                          <span className="text-lg">✓</span>
                          Promo Applied
                        </>
                      ) : (
                        <>
                          <span className="text-lg">🎉</span>
                          Apply PROMO15 for 15% off
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* Additional Information */}
                {selectedProducts.length > 0 && (
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    <h3 className="font-semibold text-gray-900">Order Notes</h3>
                    <Textarea 
                      placeholder="Add any special instructions or notes for your order..." 
                      className="w-full min-h-[100px] bg-white rounded-lg border-gray-200" 
                    />
                  </div>
                )}

                {/* Price Summary */}
                {selectedProducts.length > 0 && (
                  <div className="bg-gray-900 text-white rounded-xl p-4 space-y-3">
                    <h3 className="font-semibold">Order Summary</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Subtotal</span>
                        <span>{subtotal.toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Shipping</span>
                        <span>{shipping.toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Taxes</span>
                        <span>{taxes.toFixed(2)}€</span>
                      </div>
                      {isDiscountApplied && (
                        <div className="flex justify-between text-green-400">
                          <span>Discount (15%)</span>
                          <span>-{discountAmount.toFixed(2)}€</span>
                        </div>
                      )}
                      <div className="pt-2 border-t border-gray-700 flex justify-between font-bold text-lg">
                        <span>Total</span>
                        <span>{total.toFixed(2)}€</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Checkout Button */}
                {selectedProducts.length > 0 && (
                  <Button className=" black-cursor  w-full h-14 text-lg bg-kinetic-pink hover:bg-kinetic-pink/90 text-kinetic-magenta font-semibold rounded-xl shadow-lg shadow-kinetic-pink/20">
                    Proceed to Checkout
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Navigation - Shared */}
          <div className="flex justify-around py-4 border-t bg-gray-50 mt-auto">
            <Archive className="w-5 h-5 text-blue-500" />
            <Folder className="w-5 h-5 text-blue-500" />
            <ArrowLeft className="w-5 h-5 text-blue-500" />
            <SquarePen className="w-5 h-5 text-blue-500" />
          </div>
        </div>
      </div>
    </ div>
  )
}

export default function IPhoneEmailPurchase() {
  return (
    <Suspense>
      <IPhoneEmailPurchaseContent />
    </Suspense>
  )
}